import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { tsPayStepType, PaymentInfoStatusType, ChargeOnLink2SaveType } from './payment-info.types'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { ReservationF } from '../../types/reservation-detail.types'
import { TsPayLinkType } from 'src/api/api.generated'

export const useReservationInfo = (reservation: ReservationF) => {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()

  const reservationOrigin = useMemo(() => {
    return reservation.origin.name
  }, [reservation.origin])

  const paymentInfoVisible = useMemo(() => {
    if (reservationOrigin !== 'OTA') {
      if (reservation.policy?.paymentRule.type === 'NOTREQUIRED' && !reservation.policy?.paymentRule.guaranteedCreditCard) return false
    }
    return true
  }, [reservationOrigin, reservation])

  const chargeOnLink2Save = useMemo((): ChargeOnLink2SaveType => {
    const infoItems = reservation.tsPayLinks?.filter(({ type, cardInfo }) => type === 'LINK2SAVE' && cardInfo)
    if (infoItems) {
      const lastItem = infoItems[infoItems.length - 1]
      if (lastItem) {
        const chargeInfo = lastItem.charges.find((c) => c.state === 'PENDING' || c.state === 'DONE')
        if (chargeInfo)
          return {
            allowChargeOnLink2Save: true,
            alreadyCharged: true,
            maxAmountAllowed: lastItem.amount,
            chargeAmount: chargeInfo.amount,
            chargeDate: chargeInfo.auditable.modifiedOn ?? new Date(),
          }

        return {
          allowChargeOnLink2Save: true,
          alreadyCharged: false,
          maxAmountAllowed: lastItem.amount,
        }
      }
    }

    return {
      allowChargeOnLink2Save: false,
    }
  }, [reservation])

  const steps = useMemo(() => {
    const stepList: tsPayStepType[] = []
    if (reservationOrigin !== 'OTA') {
      const links = reservation.tsPayLinks
      links?.map((l) => {
        if (l.type === 'LINK2SAVE') {
          stepList.push({ description: t('pms.reservation.payment_info.cc_guarantee_request_step'), date: l.auditable.createdOn ? formatDate(l.auditable.createdOn, 'Pp') : '' })
          if (l.cardInfo) stepList.push({ description: t('pms.reservation.payment_info.cc_guarantee_received_step'), date: l.auditable.modifiedOn ? formatDate(l.auditable.modifiedOn, 'Pp') : '' })
          else stepList.push({ description: t('pms.reservation.payment_info.cc_guarantee_request_pending_step') })
          if (chargeOnLink2Save.allowChargeOnLink2Save && chargeOnLink2Save.alreadyCharged)
            stepList.push({
              description: t('pms.reservation.payment_info.cc_guarantee_charged_step', { amount: formatCurrency(chargeOnLink2Save.chargeAmount, currency) }),
              date: formatDate(chargeOnLink2Save.chargeDate, 'Pp'),
            })
        } else {
          stepList.push({
            description: t('pms.reservation.payment_info.payment_request_step', { amount: formatCurrency(l.amount, currency) }),
            date: l.auditable.createdOn ? formatDate(l.auditable.createdOn, 'Pp') : '',
          })
          if (l.charges && l.charges.length > 0) {
            l.charges.map((c) => {
              stepList.push({
                description: c.error ? t('pms.reservation.payment_info.error_payment') : t('pms.reservation.payment_info.payment_received_step'),
                date: c.auditable.modifiedOn ? formatDate(c.auditable.modifiedOn, 'Pp') : '',
                error: c.error?.message ?? undefined,
              })
            })
          } else {
            stepList.push({ description: t('pms.reservation.payment_info.payment_request_pending_step') })
          }
        }
      })
    }
    return stepList
  }, [reservationOrigin, reservation.tsPayLinks, currency, chargeOnLink2Save])

  const lastStep = useMemo(() => {
    // const lastIndex = steps.findIndex((x) => !x.date)
    // return lastIndex > 0 ? lastIndex : steps.length
    let x = steps.length - 1
    let lastIndex = null
    while (x > 0 && lastIndex === null) {
      if (!steps[x]?.date) {
        lastIndex = x
      }
      x = x - 1
    }
    return lastIndex ? lastIndex : steps.length
  }, [steps])

  const [status, setStatus] = useState<PaymentInfoStatusType>('PENDING')

  useEffect(() => {
    if (reservationOrigin === 'OTA' && reservation.portalInfo) {
      setStatus('COMPLETED')
    } else {
      if (steps && steps.length > 0 && lastStep) {
        if (steps.some((step, index) => step.error && index === lastStep)) setStatus('ERROR')
        else if (lastStep >= steps.length) setStatus('COMPLETED')
        else setStatus('PENDING')
      } else {
        setStatus('ND')
      }
    }
  }, [reservationOrigin, steps, lastStep, reservation])

  const tsPayReqestType = useMemo((): TsPayLinkType | undefined => {
    if (reservation.policy?.paymentRule.type === 'NOTREQUIRED' && reservation.policy.paymentRule.guaranteedCreditCard) return 'LINK2SAVE'
    else if (reservation.policy?.paymentRule.type !== 'NOTREQUIRED') return 'LINK2PAY'
    return undefined
  }, [reservation.policy])

  // TEMP // FIXME: Eliminare, variabile creata solo ai fini di sviluppo ++++++++++++++++++++
  // const fakeSteps: tsPayStepType[] = [
  //   {
  //     description: 'this is a test',
  //     date: new Date().toISOString(),
  //   },
  //   {
  //     description: 'this is a test',
  //     date: new Date().toISOString(),
  //   },
  // ]

  // if (chargeOnLink2Save.allowChargeOnLink2Save && chargeOnLink2Save.alreadyCharged)
  //   fakeSteps.push({
  //     description: t('pms.reservation.payment_info.cc_guarantee_charged_step', { amount: formatCurrency(chargeOnLink2Save.chargeAmount, currency) }),
  //     date: formatDate(chargeOnLink2Save.chargeDate, 'Pp'),
  //   })

  // const fakeStatus: PaymentInfoStatusType = 'PENDING'

  //return { reservationOrigin, paymentInfoVisible, paymentSteps: fakeSteps, lastPaymentStepTodo: 3, paymentStatus: fakeStatus, tsPayReqestType, chargeOnLink2Save: chargeOnLink2Save }
  // TEMP // FIXME: Eliminare, variabile creata solo ai fini di sviluppo ++++++++++++++++++++

  return { reservationOrigin, paymentInfoVisible, paymentSteps: steps, lastPaymentStepTodo: lastStep, paymentStatus: status, tsPayReqestType, chargeOnLink2Save }
}
